import { PrinterOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import html2canvas from "html2canvas";
import { FC, useEffect, useRef, useState } from "react";
import { Company } from "../../../../interfaces/company";
import { getAllCajas } from "../../../../services/cajaService";
import { getEmpresa } from "../../../../services/empresaService";
import { getInvoiceById } from "../../../../services/invoiceService";
import { getTPVByNoOrId } from "../../../../services/tpvService";
import "../../InvoiceStyles.css";
import {
  getPeruDocumentKind,
  PERU_TICKET_CONFIG,
  PeruDocumentKind,
} from "./invoiceTicketUtils";
import PeruTicketBody from "./PeruTicketBody";
import "./InvoiceTicket.css";

interface PeruTicketProps {
  open: boolean;
  onClose: () => void;
  invoice: any;
  lines: any[];
  caja?: string;
  turno?: number | string;
  paymentLines?: any[];
  selectedClient?: any;
  selectedTPV?: any;
  country?: any;
  cajaInfo?: any;
}

const PeruTicket: FC<PeruTicketProps> = ({
  open,
  onClose,
  invoice,
  lines = [],
  selectedClient,
  selectedTPV,
  caja,
  cajaInfo,
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [empresaData, setEmpresaData] = useState<Company | null>(null);
  const [enrichedInvoice, setEnrichedInvoice] = useState<any>(null);
  const [resolvedTPV, setResolvedTPV] = useState(selectedTPV || null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTicketData = async () => {
      if (!invoice || !open) return;

      setIsLoading(true);
      try {
        const [empresa, currentInvoice] = await Promise.all([
          getEmpresa().catch(() => null),
          !invoice.No && invoice.id
            ? getInvoiceById(invoice.id)
            : Promise.resolve(invoice),
        ]);

        let tpvData = selectedTPV || null;
        if (!tpvData && currentInvoice?.caja) {
          const cajas = await getAllCajas();
          const cajaInfoRow = cajas.find((c) => c.No === currentInvoice.caja);
          if (cajaInfoRow?.TPV) {
            tpvData = await getTPVByNoOrId(cajaInfoRow.TPV);
          }
        }

        setEmpresaData(empresa);
        setEnrichedInvoice(currentInvoice);
        setResolvedTPV(tpvData);
      } catch (error) {
        console.error("Error cargando datos del ticket de Perú", error);
        setEnrichedInvoice(invoice);
        setResolvedTPV(selectedTPV || null);
      } finally {
        setIsLoading(false);
      }
    };

    loadTicketData();
  }, [invoice, open, selectedTPV]);

  if (!open) return null;
  if (isLoading) return <div>Cargando ticket...</div>;

  const currentInvoice = enrichedInvoice || invoice;
  const documentKind: PeruDocumentKind | null = getPeruDocumentKind(
    currentInvoice,
    selectedClient,
  );

  if (!documentKind) return null;

  const config = PERU_TICKET_CONFIG[documentKind];
  const downloadFileName = `${config.downloadPrefix}-${currentInvoice?.No || "peru"}.png`;

  const captureImage = async () => {
    if (!componentRef.current) return null;

    const canvas = await html2canvas(componentRef.current, {
      scale: 3,
      logging: false,
      useCORS: true,
      backgroundColor: "#ffffff",
      width: componentRef.current.offsetWidth,
      height: componentRef.current.offsetHeight,
    });

    return canvas.toDataURL("image/png");
  };

  const printImage = async () => {
    try {
      const image = await captureImage();
      if (!image) return;

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        (window as any).messageApi?.warning(
          "El navegador bloqueó la ventana emergente. Por favor, permítelas para imprimir.",
        );
        return;
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${config.printTitle} ${currentInvoice?.No || ""}</title>
            <style>
              @page { margin: 0; size: 80mm auto; }
              html, body { margin: 0; padding: 0; width: 80mm; background: #fff; }
              img { width: 100%; height: auto; display: block; }
              @media print {
                header, footer { display: none !important; }
                html, body { height: auto !important; overflow: visible !important; }
              }
            </style>
          </head>
          <body>
            <img src="${image}" alt="Ticket" />
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() { window.close(); };
                  setTimeout(function() { window.close(); }, 1000);
                }, 300);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error("Error al imprimir:", error);
      (window as any).messageApi?.error("Error al procesar la imagen del ticket");
    }
  };

  const downloadTicketImage = async () => {
    try {
      const image = await captureImage();
      if (!image) return;

      const link = document.createElement("a");
      link.download = downloadFileName;
      link.href = image;
      link.click();
      (window as any).messageApi?.success("Imagen descargada correctamente");
    } catch (error) {
      console.error("Error al descargar imagen:", error);
      (window as any).messageApi?.error("Error al descargar la imagen");
    }
  };

  return (
    <Modal
      title={config.previewTitle}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="download" onClick={downloadTicketImage}>
          Descargar Imagen
        </Button>,
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={printImage}
        >
          Imprimir
        </Button>,
      ]}
      width={480}
      centered
      className="ticket-preview-modal ticket-preview-modal-peru"
      styles={{ body: { padding: "8px 12px 0" } }}
    >
      <div className="pe-ticket-preview-scroll">
        <PeruTicketBody
          ref={componentRef}
          documentKind={documentKind}
          invoice={currentInvoice}
          lines={lines}
          selectedClient={selectedClient}
          selectedTPV={resolvedTPV}
          empresaData={empresaData}
          caja={caja}
          cajaInfo={cajaInfo}
        />
      </div>
    </Modal>
  );
};

export default PeruTicket;
